import { useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined'
import type { AxiosError } from 'axios'

import { EmptyState, Loader, SectionCard, StatusPill } from '~/shared/ui'
import {
  usePracticeHours,
  useSavePracticeHours,
  type PracticeHours,
  type PracticeHoursPayload,
  type WeeklyHoursDay,
} from './PracticeHoursService'

/**
 * The batch-save 400 body. `weekly_hours` is keyed by day so each message lands on its own row;
 * `holidays` is a single message about the set as a whole.
 */
interface SaveErrorBody {
  detail?: {
    weekly_hours?: Record<string, string> | string
    holidays?: string
  }
}

interface DraftHoliday {
  /**
   * Row identity for React. Two unsaved holidays can share a date, so neither the id (absent
   * until saved) nor the date is unique on its own.
   */
  localKey: string
  /** Absent until the holiday has been saved. */
  id?: number
  name: string
  date: string
}

interface EditState {
  weeklyHours: WeeklyHoursDay[]
  holidays: DraftHoliday[]
}

const toEditState = (loaded: PracticeHours): EditState => ({
  weeklyHours: loaded.weekly_hours.map(day => ({ ...day })),
  holidays: loaded.holidays.map(holiday => ({ ...holiday, localKey: `saved-${holiday.id}` })),
})

/**
 * A closed day's times are kept in state so reopening restores them, but they describe nothing
 * while the day is closed — drop them wherever the state is compared or sent.
 */
const withoutClosedDayTimes = (weeklyHours: WeeklyHoursDay[]): WeeklyHoursDay[] =>
  weeklyHours.map(day => (day.is_open ? day : { ...day, from_hour: null, to_hour: null }))

const toPayload = ({ weeklyHours, holidays }: EditState): PracticeHoursPayload => ({
  weekly_hours: withoutClosedDayTimes(weeklyHours),
  holidays: holidays.map(holiday =>
    holiday.id === undefined
      ? { name: holiday.name, date: holiday.date }
      : { id: holiday.id, name: holiday.name, date: holiday.date },
  ),
})

/** The saveable state, with render-only bookkeeping stripped, for the unsaved-changes check. */
const comparable = (state: EditState) => ({
  weeklyHours: withoutClosedDayTimes(state.weeklyHours),
  holidays: state.holidays.map(({ localKey: _localKey, ...holiday }) => holiday),
})

/** The `<input type="time">` value is HH:MM; the API's is HH:MM:SS. */
const toTimeInputValue = (time: string | null) => (time ? time.slice(0, 5) : '')
const fromTimeInputValue = (value: string) => (value ? `${value}:00` : null)

const todayIsoDate = () => {
  const now = new Date()
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-')
}

/** Formats an ISO date without timezone shifting, which `new Date('2026-11-26')` would do. */
const formatHolidayDate = (isoDate: string) => {
  const [year = NaN, month = NaN, day = NaN] = isoDate.split('-').map(Number)
  const parsed = new Date(year, month - 1, day)
  return {
    monthAbbr: parsed.toLocaleDateString('en-US', { month: 'short' }),
    dayOfMonth: day,
    longDate: parsed.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
  }
}

/**
 * Ops screen for configuring when the practice is open: a weekly card with a per-day open/closed
 * toggle and open/close times, and a holidays card of all-day closures. Both cards edit local state
 * and commit together through one batch save, so partial states never reach the server.
 *
 * All validation authority is server-side — the times are pre-checked here only for instant
 * feedback, and the messages rendered per day are the ones the API returned.
 */
const PracticeHoursSettings = () => {
  const { data: loaded, isPending: isLoading } = usePracticeHours()
  const save = useSavePracticeHours()

  // Keyed by the loaded state so a refetch (or a successful save) reseeds the form.
  const [edits, setEdits] = useState<EditState | null>(null)
  const [loadedSnapshot, setLoadedSnapshot] = useState<PracticeHours | null>(null)
  const [isAddingHoliday, setIsAddingHoliday] = useState(false)
  const [showPastHolidays, setShowPastHolidays] = useState(false)
  const [draftName, setDraftName] = useState('')
  const [draftDate, setDraftDate] = useState('')
  // Counter rather than the date, so two unsaved holidays on one date stay distinct rows.
  const nextDraftKey = useRef(0)

  if (loaded && loaded !== loadedSnapshot) {
    setLoadedSnapshot(loaded)
    setEdits(toEditState(loaded))
  }

  const serverErrors = save.isError
    ? ((save.error as AxiosError<SaveErrorBody>).response?.data?.detail ?? {})
    : {}
  const dayErrors = typeof serverErrors.weekly_hours === 'object' ? serverErrors.weekly_hours : {}
  const weeklyHoursError =
    typeof serverErrors.weekly_hours === 'string' ? serverErrors.weekly_hours : undefined

  const today = todayIsoDate()
  const { upcomingHolidays, pastHolidays } = useMemo(() => {
    const sorted = [...(edits?.holidays ?? [])].sort((a, b) => a.date.localeCompare(b.date))
    return {
      upcomingHolidays: sorted.filter(holiday => holiday.date >= today),
      pastHolidays: sorted.filter(holiday => holiday.date < today).reverse(),
    }
  }, [edits?.holidays, today])

  if (isLoading || !edits || !loadedSnapshot) {
    return <Loader />
  }

  const isDirty =
    JSON.stringify(comparable(edits)) !== JSON.stringify(comparable(toEditState(loadedSnapshot)))

  /**
   * Apply an edit and drop any rejected save's messages: they describe the payload that was sent,
   * so leaving them up would pin a stale complaint to a field the operator has just corrected.
   * The unsaved-changes bar is unaffected — it tracks the state, not the last request.
   */
  const applyEdit = (update: (current: EditState) => EditState) => {
    if (save.isError) {
      save.reset()
    }
    setEdits(current => (current === null ? null : update(current)))
  }

  const updateDay = (dayOfWeek: string, patch: Partial<WeeklyHoursDay>) =>
    applyEdit(current => ({
      ...current,
      weeklyHours: current.weeklyHours.map(day =>
        day.day_of_week === dayOfWeek ? { ...day, ...patch } : day,
      ),
    }))

  // Opening a day that has never had times needs a starting pair, or there is nothing to submit.
  // Closing keeps whatever times the day had, so toggling back restores them rather than the
  // default; `withoutClosedDayTimes` keeps them out of the comparison and the payload meanwhile.
  const toggleDay = (day: WeeklyHoursDay) =>
    updateDay(day.day_of_week, {
      is_open: !day.is_open,
      from_hour: day.from_hour ?? '08:00:00',
      to_hour: day.to_hour ?? '18:00:00',
    })

  const removeHoliday = (holiday: DraftHoliday) =>
    applyEdit(current => ({
      ...current,
      holidays: current.holidays.filter(candidate => candidate.localKey !== holiday.localKey),
    }))

  const addHoliday = () => {
    const localKey = `new-${nextDraftKey.current}`
    nextDraftKey.current += 1

    applyEdit(current => ({
      ...current,
      holidays: [...current.holidays, { localKey, name: draftName.trim(), date: draftDate }],
    }))
    setIsAddingHoliday(false)
    setDraftName('')
    setDraftDate('')
  }

  const cancelAddHoliday = () => {
    setIsAddingHoliday(false)
    setDraftName('')
    setDraftDate('')
  }

  const discard = () => {
    setEdits(toEditState(loadedSnapshot))
    save.reset()
    cancelAddHoliday()
  }

  const renderHolidayRow = (holiday: DraftHoliday, isPast: boolean) => {
    const { monthAbbr, dayOfMonth, longDate } = formatHolidayDate(holiday.date)
    return (
      <Box
        key={holiday.localKey}
        data-testid="holiday-row"
        sx={theme => ({
          display: 'grid',
          gridTemplateColumns: '44px 1fr auto',
          alignItems: 'center',
          gap: 1.75,
          px: 3,
          py: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          ...(isPast ? { opacity: 0.55 } : {}),
        })}
      >
        <Box
          aria-hidden
          sx={theme => ({
            width: 44,
            height: 44,
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: isPast ? theme.palette.tones.neutral.bg : theme.palette.tones.info.bg,
            color: isPast ? theme.palette.tones.neutral.fg : theme.palette.tones.info.fg,
          })}
        >
          <Box
            component="span"
            sx={{
              fontSize: '1rem',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              lineHeight: 1.2,
            }}
          >
            {monthAbbr}
          </Box>
          <Box
            component="span"
            sx={{ fontSize: '1.7rem', fontWeight: 600, lineHeight: 1.1, color: 'text.primary' }}
          >
            {dayOfMonth}
          </Box>
        </Box>
        <Box>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 500 }}>{holiday.name}</Typography>
          <Typography sx={{ fontSize: '1.3rem', color: 'text.secondary' }}>{longDate}</Typography>
        </Box>
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
          {!isPast && <StatusPill tone="neutral" label="Closed" />}
          <IconButton
            aria-label={`Remove ${holiday.name}`}
            onClick={() => removeHoliday(holiday)}
            data-testid="remove-holiday"
            size="small"
            sx={theme => ({ '&:hover': { color: theme.palette.tones.error.fg } })}
          >
            <CloseRoundedIcon sx={{ fontSize: '1.8rem' }} />
          </IconButton>
        </Stack>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3, width: '100%', maxWidth: 760 }} data-testid="practice-hours-settings">
      <Typography sx={{ fontSize: '2.2rem', fontWeight: 600, mb: 0.5 }}>Practice Hours</Typography>
      <Typography sx={{ fontSize: '1.4rem', color: 'text.secondary', mb: 2.5 }}>
        Set the days and times the practice is open, and add holidays when the practice is closed.
      </Typography>

      <SectionCard disablePadding sx={{ mb: 2.5 }}>
        <Box sx={{ px: 3, pt: 2.25, pb: 1.75 }}>
          <Typography sx={{ fontSize: '1.6rem', fontWeight: 600 }}>Weekly hours</Typography>
          <Typography sx={{ fontSize: '1.3rem', color: 'text.secondary' }}>
            Regular open and close times by day of week
          </Typography>
        </Box>
        {weeklyHoursError && (
          <Typography
            role="alert"
            sx={theme => ({
              px: 3,
              py: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.tones.error.bg,
              color: theme.palette.tones.error.fg,
              fontSize: '1.3rem',
            })}
          >
            {weeklyHoursError}
          </Typography>
        )}
        {edits.weeklyHours.map(day => (
          <Box
            key={day.day_of_week}
            data-testid={`weekly-hours-row-${day.day_of_week}`}
            sx={theme => ({
              display: 'grid',
              gridTemplateColumns: '52px 110px 1fr',
              alignItems: 'center',
              gap: 1,
              px: 3,
              py: 0.75,
              minHeight: 48,
              borderTop: `1px solid ${theme.palette.divider}`,
            })}
          >
            <Switch
              checked={day.is_open}
              onChange={() => toggleDay(day)}
              slotProps={{ input: { 'aria-label': `Toggle ${day.day_of_week}` } }}
            />
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 500 }}>{day.day_of_week}</Typography>
            {day.is_open ? (
              <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                <TextField
                  type="time"
                  size="small"
                  value={toTimeInputValue(day.from_hour)}
                  onChange={event =>
                    updateDay(day.day_of_week, {
                      from_hour: fromTimeInputValue(event.target.value),
                    })
                  }
                  error={!!dayErrors[day.day_of_week]}
                  slotProps={{ htmlInput: { 'aria-label': `${day.day_of_week} open time` } }}
                />
                <Typography sx={{ fontSize: '1.3rem', color: 'text.secondary' }}>to</Typography>
                <TextField
                  type="time"
                  size="small"
                  value={toTimeInputValue(day.to_hour)}
                  onChange={event =>
                    updateDay(day.day_of_week, { to_hour: fromTimeInputValue(event.target.value) })
                  }
                  error={!!dayErrors[day.day_of_week]}
                  slotProps={{ htmlInput: { 'aria-label': `${day.day_of_week} close time` } }}
                />
                {dayErrors[day.day_of_week] && (
                  <Typography
                    role="alert"
                    sx={theme => ({ fontSize: '1.3rem', color: theme.palette.tones.error.fg })}
                  >
                    {dayErrors[day.day_of_week]}
                  </Typography>
                )}
              </Stack>
            ) : (
              <Typography sx={{ fontSize: '1.4rem', color: 'text.secondary' }}>Closed</Typography>
            )}
          </Box>
        ))}
      </SectionCard>

      <SectionCard disablePadding>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, px: 3, pt: 2.25, pb: 1.75 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '1.6rem', fontWeight: 600 }}>Holidays</Typography>
            <Typography sx={{ fontSize: '1.3rem', color: 'text.secondary' }}>
              Dates the practice is closed all day
            </Typography>
          </Box>
          {!isAddingHoliday && (
            <Button
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              onClick={() => setIsAddingHoliday(true)}
              data-testid="add-holiday-trigger"
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Add holiday
            </Button>
          )}
        </Box>

        {serverErrors.holidays && (
          <Typography
            role="alert"
            sx={theme => ({
              px: 3,
              py: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.tones.error.bg,
              color: theme.palette.tones.error.fg,
              fontSize: '1.3rem',
            })}
          >
            {serverErrors.holidays}
          </Typography>
        )}

        {isAddingHoliday && (
          <Stack
            direction="row"
            spacing={1.25}
            sx={theme => ({
              alignItems: 'center',
              px: 3,
              py: 1.75,
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.pane',
            })}
          >
            <TextField
              size="small"
              label="Holiday name"
              placeholder="e.g. Thanksgiving"
              value={draftName}
              onChange={event => setDraftName(event.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              type="date"
              size="small"
              value={draftDate}
              onChange={event => setDraftDate(event.target.value)}
              slotProps={{ htmlInput: { 'aria-label': 'Holiday date' } }}
            />
            <Button
              variant="contained"
              onClick={addHoliday}
              disabled={!draftName.trim() || !draftDate}
              data-testid="add-holiday"
            >
              Add
            </Button>
            <Button color="inherit" onClick={cancelAddHoliday}>
              Cancel
            </Button>
          </Stack>
        )}

        {upcomingHolidays.length === 0 && !isAddingHoliday && (
          <Box sx={theme => ({ borderTop: `1px solid ${theme.palette.divider}` })}>
            {/* A tenant with only past holidays has holidays — say what is actually empty, or the
                copy contradicts the "Past holidays" toggle sitting right below it. */}
            {pastHolidays.length > 0 ? (
              <EmptyState icon={<EventBusyOutlinedIcon />} title="No upcoming holidays" />
            ) : (
              <EmptyState
                icon={<EventBusyOutlinedIcon />}
                title="No holidays yet"
                caption="Add a date to close the practice all day. The app shows the practice as closed and members are routed to the after-hours line."
              />
            )}
          </Box>
        )}

        {upcomingHolidays.map(holiday => renderHolidayRow(holiday, false))}

        {pastHolidays.length > 0 && (
          <>
            <Box
              component="button"
              type="button"
              onClick={() => setShowPastHolidays(previous => !previous)}
              aria-expanded={showPastHolidays}
              data-testid="toggle-past-holidays"
              sx={theme => ({
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                width: '100%',
                px: 3,
                py: 1.25,
                border: 'none',
                borderTop: `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.pane',
                color: 'text.secondary',
                cursor: 'pointer',
                font: 'inherit',
                fontSize: '1.3rem',
                fontWeight: 600,
                textAlign: 'left',
              })}
            >
              <ChevronRightRoundedIcon
                aria-hidden
                sx={{
                  fontSize: '1.8rem',
                  transform: showPastHolidays ? 'rotate(90deg)' : 'none',
                }}
              />
              {showPastHolidays ? 'Hide past holidays' : `Past holidays (${pastHolidays.length})`}
            </Box>
            <Collapse in={showPastHolidays} unmountOnExit>
              {pastHolidays.map(holiday => renderHolidayRow(holiday, true))}
            </Collapse>
          </>
        )}
      </SectionCard>

      {isDirty && (
        <Stack
          direction="row"
          spacing={1.5}
          data-testid="unsaved-changes-bar"
          sx={theme => ({
            alignItems: 'center',
            position: 'sticky',
            bottom: theme.spacing(2),
            mt: 2.5,
            px: 2.5,
            py: 1.5,
            bgcolor: 'background.default',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '12px',
            boxShadow: theme.shadows[4],
          })}
        >
          <Typography sx={{ flex: 1, fontSize: '1.4rem', color: 'text.secondary' }}>
            You have unsaved changes
          </Typography>
          <Button color="inherit" onClick={discard} disabled={save.isPending}>
            Discard
          </Button>
          <Button
            variant="contained"
            onClick={() => save.mutate(toPayload(edits))}
            disabled={save.isPending}
            data-testid="save-practice-hours"
          >
            Save changes
          </Button>
        </Stack>
      )}
    </Box>
  )
}

export default PracticeHoursSettings
